import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';

export function ContratoDialog({ 
  open, 
  onOpenChange, 
  form, 
  setForm, 
  onSubmit, 
  onReset,
  clientes = [],
  onAddServico,
  onUpdateServico,
  onRemoveServico
}) {
  const handleClose = () => {
    onOpenChange(false);
    onReset?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
          <DialogHeader>
            <DialogTitle>Novo Contrato</DialogTitle>
            <DialogDescription>Crie um novo contrato de prestação de serviços</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cliente *</Label>
                <Select 
                  value={form.cliente_id || undefined} 
                  onValueChange={(v) => setForm(prev => ({ ...prev, cliente_id: v }))}
                >
                  <SelectTrigger data-testid="select-cliente-contrato">
                    <SelectValue placeholder="Selecione o cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientes.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo de Contrato</Label>
                <Select 
                  value={form.tipo} 
                  onValueChange={(v) => setForm(prev => ({ ...prev, tipo: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mensal">Mensal (Valor Fixo)</SelectItem>
                    <SelectItem value="producao">Por Produção</SelectItem>
                    <SelectItem value="misto">Misto (Mensal + Produção)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Valor Mensal (R$)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={form.valor_mensal}
                  onChange={(e) => setForm(prev => ({ ...prev, valor_mensal: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Data Início</Label>
                <Input 
                  type="date"
                  value={form.data_inicio}
                  onChange={(e) => setForm(prev => ({ ...prev, data_inicio: e.target.value }))}
                />
              </div>
              <div>
                <Label>Data Fim</Label>
                <Input 
                  type="date"
                  value={form.data_fim}
                  onChange={(e) => setForm(prev => ({ ...prev, data_fim: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Índice de Reajuste</Label>
                <Select 
                  value={form.indice_reajuste} 
                  onValueChange={(v) => setForm(prev => ({ ...prev, indice_reajuste: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="igpm">IGP-M</SelectItem>
                    <SelectItem value="ipca">IPCA</SelectItem>
                    <SelectItem value="inpc">INPC</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Periodicidade do Reajuste</Label>
                <Select 
                  value={form.periodicidade_reajuste} 
                  onValueChange={(v) => setForm(prev => ({ ...prev, periodicidade_reajuste: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="anual">Anual</SelectItem>
                    <SelectItem value="semestral">Semestral</SelectItem>
                    <SelectItem value="trimestral">Trimestral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Serviços Inclusos</Label>
                <Button type="button" size="sm" variant="outline" onClick={onAddServico}>
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar Serviço
                </Button>
              </div>
              
              {form.servicos.map((servico, index) => (
                <div key={index} className="flex gap-2 mb-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs">Descrição</Label>
                    <Input 
                      value={servico.descricao}
                      onChange={(e) => onUpdateServico(index, 'descricao', e.target.value)}
                      placeholder="Descrição do serviço"
                    />
                  </div>
                  <div className="w-28">
                    <Label className="text-xs">Valor Unit.</Label>
                    <Input 
                      type="number"
                      step="0.01"
                      value={servico.valor_unitario}
                      onChange={(e) => onUpdateServico(index, 'valor_unitario', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="w-20">
                    <Label className="text-xs">Qtd Inclusa</Label>
                    <Input 
                      type="number"
                      value={servico.quantidade_inclusa}
                      onChange={(e) => onUpdateServico(index, 'quantidade_inclusa', parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <Button type="button" size="icon" variant="ghost" onClick={() => onRemoveServico(index)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
            
            <div>
              <Label>Observações</Label>
              <Textarea 
                value={form.observacoes}
                onChange={(e) => setForm(prev => ({ ...prev, observacoes: e.target.value }))}
                placeholder="Observações gerais..."
              />
            </div>
            
            <div>
              <Label>Cláusulas Especiais</Label>
              <Textarea 
                value={form.clausulas_especiais}
                onChange={(e) => setForm(prev => ({ ...prev, clausulas_especiais: e.target.value }))}
                placeholder="Cláusulas especiais do contrato..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" data-testid="btn-salvar-contrato">
              Criar Contrato
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
